import React from 'react';
import { Pencil, Percent, Ruler, Trash2 } from 'lucide-react';

const actionIconBtnClass =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#475569] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CBD5E1] focus-visible:ring-offset-1';

const initialsThemes = [
  'bg-[#E7F2EF] border-[#BFE6DE] text-[#2A565E]',
  'bg-[#EEF2FF] border-[#D6DBFF] text-[#4F46E5]',
  'bg-[#FFF4E8] border-[#FBD7B2] text-[#B45309]',
  'bg-[#F3E8FF] border-[#E2CCFF] text-[#7E22CE]',
  'bg-[#EAF6FF] border-[#C5E4FA] text-[#0369A1]',
  'bg-[#F0FDF4] border-[#BBF7D0] text-[#166534]',
  'bg-[#FDF4FF] border-[#E9D5FF] text-[#86198F]'
];

const PlcCard = ({ plc, onEdit, onDelete }) => {
  const plcInitials = (plc?.name || 'PL')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const avatarSeed = String(plc?.id ?? plc?.name ?? 'PL')
    .split('')
    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const initialsTheme = initialsThemes[avatarSeed % initialsThemes.length];

  const modeLabel =
    plc.mode === 'percent' ? 'Percent' : plc.mode === 'numeric' ? 'Numeric rate' : 'PLC';

  const hasPercentRate = plc.mode === 'percent' && plc.price != null && plc.price !== '';
  const hasUnitRate = plc.mode === 'numeric' && plc.price != null && plc.price !== '';
  const isGstInclusive = plc.gstInclusive === true || plc.gstInclusive === 'true';

  let percentDisplay = '';
  if (hasPercentRate) {
    const n = Number(plc.price);
    percentDisplay = Number.isFinite(n) ? `${parseFloat(n.toFixed(4))}%` : `${plc.price}%`;
  }

  const unitDisplay = hasUnitRate ? `${plc.price}${plc.numericRateUnit || ''}` : '';

  const blockParts = String(plc.blockName || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <article className=" min-h-[200px]  group bg-white rounded-2xl border border-[#E2E8F0] p-4 flex flex-col gap-2.5 shadow-sm relative transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <button
          type="button"
          aria-label="Edit PLC"
          title="Edit"
          className={actionIconBtnClass}
          onClick={() => onEdit?.(plc.id)}>
          <Pencil size={17} strokeWidth={1.75} className="text-current" />
        </button>
        {plc.id ? (
          <button
            type="button"
            aria-label="Delete PLC"
            title="Delete"
            className={actionIconBtnClass}
            onClick={() => onDelete?.(plc.id)}>
            <Trash2 size={17} strokeWidth={1.75} className="text-current" />
          </button>
        ) : null}
      </div>

      <div className="flex-1 flex flex-col gap-2.5">
        <div className="flex items-start gap-3">
          <div
            className={`h-14 w-14 rounded-xl border flex items-center justify-center text-lg font-semibold shrink-0 ${initialsTheme}`}>
            {plcInitials || 'PL'}
          </div>
          <div className="min-w-0">
            <p
              className="text-[18px] leading-tight font-semibold text-[#1A202C] truncate"
              title={plc.name || 'PLC'}>
              {plc.name || '---'}
            </p>
            <span className="mt-1 inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold bg-[#F3E8FF] text-[#6B46C1]">
              {modeLabel}
            </span>
          </div>
        </div>

        {plc.description ? (
          <p className="text-xs text-[#94A3B8]" title={plc.description}>
            {plc.description}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          {hasPercentRate && (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#E6FFFA] text-[#2C7A7B]">
              <Percent size={12} strokeWidth={2.5} />
              Rate: {percentDisplay}
            </span>
          )}
          {hasUnitRate && (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EBF4FF] text-[#5A67D8]">
              <Ruler size={12} strokeWidth={2.5} />
              Unit: {unitDisplay}
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${
              isGstInclusive ? 'bg-[#E6FFFA] text-[#2C7A7B]' : 'bg-[#FFF5F5] text-[#C53030]'
            }`}>
            {isGstInclusive ? 'Inclusive GST' : 'Exclusive GST'}
          </span>
          {blockParts.length > 0 ? (
            blockParts.map((blockName, idx) => (
              <span
                key={`${plc.id}-block-${idx}`}
                className="inline-flex max-w-full items-center rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-1 text-[12px] font-medium text-[#475569] truncate"
                title={blockName}>
                {blockName}
              </span>
            ))
          ) : (
            <span className="inline-flex rounded-full border border-dashed border-[#CBD5E1] bg-white px-2.5 py-1 text-[12px] text-[#94A3B8]">
              No associated blocks
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default PlcCard;
